import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  Skeleton,
  IconButton,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Paper,
  LinearProgress,
  Checkbox,
  Radio,
  RadioGroup,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  Person as PersonIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  Home as HomeIcon,
  Badge as BadgeIcon,
  Warning as WarningIcon,
  AttachFile as AttachFileIcon,
  Speed as MeterIcon,
  Search as SearchIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { customersApi, customerDocumentsApi, metersApi } from '../../services/api';
import { validateCodiceFiscale, parseCodiceFiscale } from '../../utils/codiceFiscale';

// Mappa campi OCR → label + categoria. I campi "appliable" hanno checkbox
const OCR_APPLIABLE_FIELDS = [
  { key: 'cognome', label: 'Cognome', category: 'anagrafica' },
  { key: 'nome', label: 'Nome', category: 'anagrafica' },
  { key: 'codice_fiscale', label: 'Codice Fiscale', category: 'anagrafica' },
  { key: 'data_nascita', label: 'Data Nascita', category: 'anagrafica', displayKey: 'data_nascita_display' },
  { key: 'luogo_nascita', label: 'Luogo Nascita', category: 'anagrafica' },
  { key: 'indirizzo', label: 'Via / Indirizzo', category: 'indirizzo' },
  { key: 'comune_residenza', label: 'Città / Comune', category: 'indirizzo' },
  { key: 'cap', label: 'CAP', category: 'indirizzo' },
  { key: 'provincia', label: 'Provincia', category: 'indirizzo' },
];

const OCR_INFO_FIELDS = [
  { key: 'tipo_documento', label: 'Tipo Documento' },
  { key: 'numero_documento', label: 'Numero Documento' },
  { key: 'scadenza_documento', label: 'Scadenza' },
  { key: 'data_emissione', label: 'Data Emissione' },
  { key: 'ente_emittente', label: 'Ente Emittente' },
];

const SIGNER_ROLE_OPTIONS = [
  { value: 'legal_representative', label: 'Legale rappresentante' },
  { value: 'owner', label: 'Titolare' },
  { value: 'president', label: 'Presidente' },
  { value: 'sole_director', label: 'Amministratore unico' },
  { value: 'managing_director', label: 'Amministratore delegato' },
  { value: 'managing_partner', label: 'Socio amministratore' },
  { value: 'proxy', label: 'Procuratore' },
  { value: 'delegate', label: 'Delegato alla firma' },
  { value: 'liquidator', label: 'Liquidatore' },
  { value: 'other', label: 'Altro' },
];
const CustomerForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors, isDirty } } = useForm({
    defaultValues: {
      customer_type: 'private',
      first_name: '',
      last_name: '',
      business_name: '',
      fiscal_code: '',
      vat_number: '',
      birth_place: '',
      birth_date: '',
      signer_role: 'legal_representative',
      signer_role_custom: '',
      signer_first_name: '',
      signer_last_name: '',
      signer_fiscal_code: '',
      signer_birth_date: '',
      signer_birth_place: '',
      signer_address: '',
      signer_postal_code: '',
      signer_city: '',
      signer_province: '',
      // Indirizzo residenza
      address_residenza: '',
      postal_code_residenza: '',
      city_residenza: '',
      province_residenza: '',
      // Indirizzo recapito bollette
      address: '',
      postal_code: '',
      city: '',
      province: '',
      phone: '',
      mobile: '',
      fax: '',
      email: '',
      email_secondaria: '',
      pec: '',
      sdi_code: '',
      notes: '',
      is_active: true,
      // Nuovi campi per preferenze fatturazione
      invio_email: false,
      modalita_pagamento: 'bollettino',
      iban: '',
      sconto_legge_448: false,
      mandato_sepa_id: '',
      mandato_sepa_data: '',
      whatsapp_number: '',
      whatsapp_opt_in: false,
    },
  });

  const customerType = watch('customer_type');
  const modalitaPagamento = watch('modalita_pagamento');
  const fiscalCode = watch('fiscal_code');
  const signerRole = watch('signer_role');
  
  // Stato per validazione CF
  const [cfValidation, setCfValidation] = useState({ valid: null, error: null, loading: false });
  
  // Stato per cliente bozza (draft)
  const [draftInfo, setDraftInfo] = useState({ id: null, cod_cli: null, loading: false, error: null });
  
  // Stato per documenti e OCR
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [ocrResults, setOcrResults] = useState([]);
  const [mergedOcrSelection, setMergedOcrSelection] = useState({});
  const [ocrApplied, setOcrApplied] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const fileInputRef = useRef(null);

  // Stato per documenti aggiuntivi (senza OCR)
  const [extraDocs, setExtraDocs] = useState([]);
  const [uploadingExtraDocs, setUploadingExtraDocs] = useState([]);
  const [extraDocDescription, setExtraDocDescription] = useState('');
  const extraFileInputRef = useRef(null);

  // Stato per associazione contatore
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [meterOptions, setMeterOptions] = useState([]);
  const [meterInputValue, setMeterInputValue] = useState('');
  const [meterSearchLoading, setMeterSearchLoading] = useState(false);
  const meterSearchTimeout = useRef(null);

  // Computa dati OCR unificati da tutti i risultati documenti
  const mergedOcrData = useMemo(() => {
    if (ocrResults.length === 0) return null;

    const fields = {};
    OCR_APPLIABLE_FIELDS.forEach(field => {
      const values = [];
      ocrResults.forEach(ocr => {
        const val = ocr.extracted[field.key];
        if (!val) return;
        const displayVal = field.displayKey ? (ocr.extracted[field.displayKey] || val) : val;
        const normalized = String(val).toUpperCase().trim();
        const existing = values.find(v => String(v.value).toUpperCase().trim() === normalized);
        if (existing) {
          existing.sources.push(ocr.documentId);
        } else {
          values.push({ value: val, displayValue: displayVal, sources: [ocr.documentId] });
        }
      });
      if (values.length > 0) {
        fields[field.key] = { values, conflict: values.length > 1 };
      }
    });

    // Info documento raggruppate per documento
    const infoByDoc = ocrResults.map(ocr => ({
      documentId: ocr.documentId,
      fields: OCR_INFO_FIELDS.filter(f => ocr.extracted[f.key]).map(f => ({
        key: f.key, label: f.label, value: ocr.extracted[f.key],
      })),
      confidence: ocr.confidence,
      ocrEngine: ocr.ocrEngine,
    })).filter(d => d.fields.length > 0);

    const hasConflicts = Object.values(fields).some(f => f.conflict);
    return { fields, infoByDoc, totalDocs: ocrResults.length, hasConflicts };
  }, [ocrResults]);

  // Inizializza/aggiorna selezione quando mergedOcrData cambia
  useEffect(() => {
    if (!mergedOcrData) {
      setMergedOcrSelection({});
      return;
    }
    setMergedOcrSelection(prev => {
      const next = {};
      Object.entries(mergedOcrData.fields).forEach(([key, data]) => {
        if (prev[key] && data.values.some(v => v.value === prev[key].selectedValue)) {
          next[key] = prev[key];
        } else {
          next[key] = { checked: true, selectedValue: data.values[0].value };
        }
      });
      return next;
    });
  }, [mergedOcrData]);

  // Label per documento sorgente (usato nei conflitti)
  const getDocSourceLabel = (docId) => {
    const doc = uploadedDocs.find(d => d.id === docId);
    if (doc) {
      const name = doc.filename || doc.original_name || '';
      return name.length > 25 ? name.substring(0, 22) + '...' : name;
    }
    return `Doc ${ocrResults.findIndex(r => r.documentId === docId) + 1}`;
  };

  // Crea cliente bozza all'apertura del form (solo per nuovi clienti)
  useEffect(() => {
    const createDraft = async () => {
      if (isEdit) return; // Non creare draft se stiamo modificando
      
      setDraftInfo({ id: null, cod_cli: null, loading: true, error: null });
      
      try {
        const response = await customersApi.createDraft();
        const draft = response.data?.data;
        
        if (draft) {
          setDraftInfo({ 
            id: draft.id, 
            cod_cli: draft.cod_cli, 
            loading: false, 
            error: null 
          });
          console.log('[Draft] Creato cliente bozza:', draft);
        }
      } catch (error) {
        console.error('[Draft] Errore creazione bozza:', error);
        setDraftInfo({ 
          id: null, 
          cod_cli: null, 
          loading: false, 
          error: 'Errore nella creazione della bozza cliente' 
        });
      }
    };
    
    createDraft();
  }, [isEdit]);

  // Effetto per parsing e validazione codice fiscale
  useEffect(() => {
    const parseCF = async () => {
      if (!fiscalCode || fiscalCode.length !== 16) {
        setCfValidation({ valid: null, error: null, loading: false });
        return;
      }
      
      setCfValidation({ valid: null, error: null, loading: true });
      
      try {
        // Prima valida localmente
        const validation = validateCodiceFiscale(fiscalCode);
        if (!validation.valid) {
          setCfValidation({ valid: false, error: validation.error, loading: false });
          return;
        }
        
        // Poi estrai dati con lookup luogo
        const parsed = await parseCodiceFiscale(fiscalCode);
        console.log('CF Parsed:', parsed);
        
        if (parsed.valid) {
          // Imposta data di nascita
          if (parsed.birthDate) {
            console.log('Setting birth_date:', parsed.birthDate);
            setValue('birth_date', parsed.birthDate);
          }
          // Imposta luogo di nascita
          if (parsed.birthPlace) {
            console.log('Setting birth_place:', parsed.birthPlace);
            setValue('birth_place', parsed.birthPlace);
          }
          
          setCfValidation({ 
            valid: true, 
            error: null, 
            loading: false,
            data: parsed 
          });
        } else {
          setCfValidation({ valid: false, error: parsed.error, loading: false });
        }
      } catch (error) {
        console.error('Errore parsing CF:', error);
        setCfValidation({ valid: false, error: 'Errore durante la validazione', loading: false });
      }
    };
    
    // Debounce per evitare troppe chiamate API
    const timeoutId = setTimeout(parseCF, 500);
    return () => clearTimeout(timeoutId);
  }, [fiscalCode, setValue]);

  // Query cliente esistente
  const { data: customer, isLoading } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => customersApi.getById(id).then(res => res.data?.data || res.data),
    enabled: isEdit,
  });

  // Effetto per resettare il form quando i dati del cliente cambiano
  React.useEffect(() => {
    if (customer) {
      reset(customer);
    }
  }, [customer, reset]);

  // Mutation salva
  const saveMutation = useMutation({
    mutationFn: (data) => isEdit 
      ? customersApi.update(id, data)
      : customersApi.create(data),
    onSuccess: async (response) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (isEdit) {
        queryClient.invalidateQueries({ queryKey: ['customer', id] });
      }
      // La risposta ha struttura { success, data: { id, ... } }
      const newCustomerId = response.data?.data?.id || response.data?.id;
      
      // Se c'è un contatore selezionato, associalo al cliente e aggiorna intestatario
      if (selectedMeter && newCustomerId) {
        try {
          const customerData = response.data?.data || response.data;
          // Costruisci intestatario come fa MeterForm: ragione_sociale oppure cognome + nome
          let intestatario = '';
          if (customerData.business_name) {
            intestatario = customerData.business_name;
          } else {
            const nome = customerData.first_name || '';
            const cognome = customerData.last_name || '';
            intestatario = `${cognome} ${nome}`.trim();
          }
          await metersApi.update(selectedMeter.id, {
            customer_id: newCustomerId,
            intestatario: intestatario,
            c_nome: intestatario,
          });
        } catch (error) {
          console.error('Errore associazione contatore:', error);
        }
      }
      
      navigate(isEdit ? `/crm/clienti/${id}` : `/crm/clienti/${newCustomerId}`, { replace: true });
    },
  });

  const onSubmit = (data) => {
    // Pulisci dati non necessari
    if (data.customer_type === 'business') {
      data.first_name = '';
      data.last_name = '';
      if (data.signer_role !== 'other') {
        data.signer_role_custom = '';
      }
    } else {
      data.business_name = '';
      data.signer_role = 'legal_representative';
      data.signer_role_custom = '';
      data.signer_first_name = '';
      data.signer_last_name = '';
      data.signer_fiscal_code = '';
      data.signer_birth_date = '';
      data.signer_birth_place = '';
      data.signer_address = '';
      data.signer_postal_code = '';
      data.signer_city = '';
      data.signer_province = '';
    }
    
    // Se stiamo salvando una bozza, passa draft_id
    if (!isEdit && draftInfo.id) {
      data.draft_id = draftInfo.id;
    }
    
    saveMutation.mutate(data);
  };

  const uploadingFile = uploadingFiles.length > 0;

  // Handler per upload documento con OCR (supporta file multipli)
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    if (!draftInfo.id) {
      alert('Attendere la creazione del cliente bozza');
      return;
    }
    
    // Inizializza stato upload per ogni file
    const initialStatus = files.map(f => ({ name: f.name, status: 'uploading' }));
    setUploadingFiles(initialStatus);
    
    // Processa tutti i file in parallelo
    const uploadPromises = files.map(async (file, index) => {
      try {
        const response = await customerDocumentsApi.uploadAndOcr(draftInfo.id, file, 'Documento identità');
        const result = response.data?.data;
        
        if (result?.document) {
          setUploadedDocs(prev => [...prev, result.document]);
        }
        
        // Se OCR ha estratto dati, mostrali
        if (result?.ocr?.extracted) {
          const ocr = result.ocr;
          const docId = result.document?.id;
          setOcrResults(prev => [...prev, {
            documentId: docId,
            extracted: ocr.extracted,
            confidence: ocr.confidence,
            ocrEngine: ocr.ocr_engine,
          }]);
          setOcrApplied(false);
        }
        
        setUploadingFiles(prev => prev.map((f, i) => i === index ? { ...f, status: 'done' } : f));
      } catch (error) {
        console.error(`Errore upload ${file.name}:`, error);
        setUploadingFiles(prev => prev.map((f, i) => i === index ? { ...f, status: 'error', error: error.message } : f));
      }
    });
    
    await Promise.all(uploadPromises);
    
    // Reset input file e stato upload dopo breve delay
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setTimeout(() => setUploadingFiles([]), 1500);
  };

  // Toggle checkbox per un campo nel merged view
  const toggleMergedField = (fieldKey) => {
    setMergedOcrSelection(prev => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], checked: !prev[fieldKey]?.checked }
    }));
  };

  // Seleziona valore in caso di conflitto
  const selectConflictValue = (fieldKey, value) => {
    setMergedOcrSelection(prev => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], selectedValue: value }
    }));
  };

  // Seleziona/deseleziona tutti
  const toggleAllMerged = (value) => {
    setMergedOcrSelection(prev => {
      const next = {};
      Object.entries(prev).forEach(([key, sel]) => {
        next[key] = { ...sel, checked: value };
      });
      return next;
    });
  };

  // Applica dati OCR unificati al form
  const applyMergedOcrData = () => {
    const privateFieldToForm = {
      cognome: ['last_name'],
      nome: ['first_name'],
      codice_fiscale: ['fiscal_code'],
      data_nascita: ['birth_date'],
      luogo_nascita: ['birth_place'],
      indirizzo: ['address_residenza', 'address'],
      comune_residenza: ['city_residenza', 'city'],
      cap: ['postal_code_residenza', 'postal_code'],
      provincia: ['province_residenza', 'province'],
    };

    const businessFieldToForm = {
      cognome: ['signer_last_name'],
      nome: ['signer_first_name'],
      codice_fiscale: ['signer_fiscal_code'],
      data_nascita: ['signer_birth_date'],
      luogo_nascita: ['signer_birth_place'],
      indirizzo: ['signer_address'],
      comune_residenza: ['signer_city'],
      cap: ['signer_postal_code'],
      provincia: ['signer_province'],
    };

    const fieldToForm = customerType === 'business' ? businessFieldToForm : privateFieldToForm;

    Object.entries(mergedOcrSelection).forEach(([key, sel]) => {
      if (!sel.checked || !sel.selectedValue) return;
      const formFields = fieldToForm[key];
      if (formFields) {
        formFields.forEach(f => setValue(f, sel.selectedValue));
      }
    });

    setOcrApplied(true);
  };

  // Handler per upload documenti aggiuntivi (senza OCR)
  const handleExtraDocUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    if (!draftInfo.id) {
      alert('Attendere la creazione del cliente bozza');
      return;
    }

    const description = extraDocDescription.trim() || 'Documento allegato';
    const initialStatus = files.map(f => ({ name: f.name, status: 'uploading' }));
    setUploadingExtraDocs(initialStatus);

    const uploadPromises = files.map(async (file, index) => {
      try {
        const response = await customerDocumentsApi.upload(draftInfo.id, file, description);
        const result = response.data?.data;
        if (result) {
          setExtraDocs(prev => [...prev, result]);
        }
        setUploadingExtraDocs(prev => prev.map((f, i) => i === index ? { ...f, status: 'done' } : f));
      } catch (error) {
        console.error(`Errore upload ${file.name}:`, error);
        setUploadingExtraDocs(prev => prev.map((f, i) => i === index ? { ...f, status: 'error', error: error.message } : f));
      }
    });

    await Promise.all(uploadPromises);
    if (extraFileInputRef.current) extraFileInputRef.current.value = '';
    setExtraDocDescription('');
    setTimeout(() => setUploadingExtraDocs([]), 1500);
  };

  // Handler ricerca contatori con debounce
  const handleMeterInputChange = (event, newInputValue) => {
    setMeterInputValue(newInputValue);
    if (meterSearchTimeout.current) clearTimeout(meterSearchTimeout.current);
    if (newInputValue.length < 2) {
      setMeterOptions([]);
      return;
    }
    meterSearchTimeout.current = setTimeout(async () => {
      setMeterSearchLoading(true);
      try {
        const response = await metersApi.getAll({ search: newInputValue, limit: 30 });
        const meters = response.data?.data?.rows || response.data?.data || [];
        setMeterOptions(meters);
      } catch (error) {
        console.error('Errore ricerca contatori:', error);
      } finally {
        setMeterSearchLoading(false);
      }
    }, 300);
  };

  // Handler per eliminare documento aggiuntivo
  const handleDeleteExtraDoc = async (doc) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await customerDocumentsApi.delete(draftInfo.id, doc.id);
      setExtraDocs(prev => prev.filter(d => d.id !== doc.id));
    } catch (error) {
      console.error('Errore eliminazione documento:', error);
      alert('Errore nell\'eliminazione del documento');
    }
  };

  // Handler per eliminare documento
  const handleDeleteDocument = async (doc) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    
    try {
      await customerDocumentsApi.delete(draftInfo.id, doc.id);
      setUploadedDocs(prev => prev.filter(d => d.id !== doc.id));
      setOcrResults(prev => prev.filter(o => o.documentId !== doc.id));
    } catch (error) {
      console.error('Errore eliminazione documento:', error);
      alert('Errore nell\'eliminazione del documento');
    }
  };

  if (isEdit && isLoading) {
    return (
      <Box>
        <Skeleton height={60} sx={{ mb: 2 }} />
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              {[...Array(12)].map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton height={56} />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 0 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isMobile ? 2 : 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
          <IconButton onClick={() => navigate(isEdit ? `/crm/clienti/${id}` : '/crm/clienti')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold">
            {isEdit ? 'Modifica Cliente' : 'Nuovo Cliente'}
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        {saveMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Errore nel salvataggio: {saveMutation.error?.response?.data?.message || 'Errore sconosciuto'}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Tipo Cliente
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="customer_type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Tipo Cliente</InputLabel>
                      <Select {...field} label="Tipo Cliente">
                        <MenuItem value="private">Privato</MenuItem>
                        <MenuItem value="business">Azienda / Ditta</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Cliente Attivo"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Sezione Upload Documenti con OCR (solo per nuovi clienti) */}
        {!isEdit && (
          <Card sx={{ mb: 3, border: '2px dashed', borderColor: 'primary.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AutoAwesomeIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Carica Documento d'Identità (OCR)
                </Typography>
                {draftInfo.cod_cli && (
                  <Chip 
                    label={`Cod. Cliente: ${draftInfo.cod_cli}`} 
                    color="primary" 
                    variant="outlined" 
                    size="small"
                    sx={{ ml: 'auto' }}
                  />
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {draftInfo.loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <CircularProgress size={20} />
                  <Typography color="text.secondary">Preparazione cliente...</Typography>
                </Box>
              )}
              
              {draftInfo.error && (
                <Alert severity="error" sx={{ mb: 2 }}>{draftInfo.error}</Alert>
              )}
              
              {draftInfo.id && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Carica uno o più documenti (carta d'identità, patente, passaporto o PDF). 
                    Il sistema leggerà automaticamente i dati anagrafici con AI e compilerà il form.
                  </Typography>
                  
                  {/* Bottone upload */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                    style={{ display: 'none' }}
                    id="document-upload"
                    multiple
                  />
                  <label htmlFor="document-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={uploadingFile ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                      disabled={uploadingFile}
                      sx={{ mb: 2 }}
                    >
                      {uploadingFile 
                        ? `Caricamento ${uploadingFiles.filter(f => f.status === 'done').length}/${uploadingFiles.length}...` 
                        : 'Carica Documenti'}
                    </Button>
                  </label>
                  
                  {/* Progresso upload multipli */}
                  {uploadingFiles.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      {uploadingFiles.map((f, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          {f.status === 'uploading' && <CircularProgress size={16} />}
                          {f.status === 'done' && <CheckCircleIcon fontSize="small" color="success" />}
                          {f.status === 'error' && <Typography color="error" fontSize="small">✗</Typography>}
                          <Typography variant="body2" color={f.status === 'error' ? 'error' : 'text.secondary'}>
                            {f.name} {f.status === 'error' ? `- ${f.error || 'Errore'}` : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  
                  {/* Lista documenti caricati */}
                  {uploadedDocs.length > 0 && (
                    <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                      {uploadedDocs.map((doc) => (
                        <ListItem key={doc.id}>
                          <ListItemIcon>
                            <DescriptionIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={doc.filename}
                            secondary={`${(doc.size / 1024).toFixed(1)} KB`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton 
                              edge="end" 
                              onClick={() => handleDeleteDocument(doc)}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                  
                  {/* Risultati OCR Unificati */}
                  {mergedOcrData && (
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        mt: 2, 
                        bgcolor: ocrApplied ? 'action.hover' : 'background.paper',
                        border: '1px solid',
                        borderColor: ocrApplied ? 'success.main' : 'primary.main',
                        borderRadius: 2
                      }}
                    >
                      {/* Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <AutoAwesomeIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Dati Estratti
                          {mergedOcrData.totalDocs > 1 && ` (${mergedOcrData.totalDocs} documenti analizzati)`}
                        </Typography>
                        {ocrApplied && (
                          <Chip label="Applicati" size="small" color="success" icon={<CheckCircleIcon />} />
                        )}
                        {mergedOcrData.hasConflicts && !ocrApplied && (
                          <Chip label="Dati discordanti rilevati" size="small" color="warning" icon={<WarningIcon />} />
                        )}
                      </Box>

                      {/* Seleziona/Deseleziona tutti */}
                      {!ocrApplied && (
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <Button size="small" variant="outlined" startIcon={<SelectAllIcon />} onClick={() => toggleAllMerged(true)}>
                            Seleziona tutti
                          </Button>
                          <Button size="small" variant="outlined" startIcon={<DeselectIcon />} onClick={() => toggleAllMerged(false)}>
                            Deseleziona tutti
                          </Button>
                        </Box>
                      )}

                      {/* Sezione: Dati Anagrafici */}
                      {OCR_APPLIABLE_FIELDS.filter(f => f.category === 'anagrafica' && mergedOcrData.fields[f.key]).length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <BadgeIcon fontSize="small" color="action" />
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" textTransform="uppercase">
                              {customerType === 'business' ? 'Dati Firmatario' : 'Dati Anagrafici'}
                            </Typography>
                          </Box>
                          <Grid container spacing={1}>
                            {OCR_APPLIABLE_FIELDS.filter(f => f.category === 'anagrafica' && mergedOcrData.fields[f.key]).map(field => {
                              const data = mergedOcrData.fields[field.key];
                              const sel = mergedOcrSelection[field.key] || {};
                              return (
                                <Grid item xs={data.conflict ? 12 : 6} sm={data.conflict ? 6 : (field.key === 'codice_fiscale' ? 6 : 3)} key={field.key}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'flex-start',
                                    bgcolor: sel.checked && !ocrApplied ? (data.conflict ? 'warning.light' : 'primary.50') : 'transparent',
                                    borderRadius: 1,
                                    border: data.conflict && !ocrApplied ? '1px solid' : 'none',
                                    borderColor: 'warning.main',
                                    p: 0.5,
                                    transition: 'background-color 0.2s',
                                  }}>
                                    {!ocrApplied && (
                                      <Checkbox
                                        size="small"
                                        checked={!!sel.checked}
                                        onChange={() => toggleMergedField(field.key)}
                                        sx={{ p: 0.25, mr: 0.5, mt: 0.25 }}
                                      />
                                    )}
                                    {ocrApplied && sel.checked && (
                                      <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 0.5, mt: 0.5 }} />
                                    )}
                                    <Box sx={{ flex: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">{field.label}</Typography>
                                        {data.conflict && (
                                          <Chip label="Conflitto" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                                        )}
                                      </Box>
                                      {!data.conflict ? (
                                        <Typography 
                                          variant="body2" 
                                          fontWeight="bold"
                                          sx={field.key === 'codice_fiscale' ? { fontFamily: 'monospace' } : {}}
                                        >
                                          {data.values[0].displayValue}
                                        </Typography>
                                      ) : (
                                        <RadioGroup
                                          value={sel.selectedValue || ''}
                                          onChange={(e) => selectConflictValue(field.key, e.target.value)}
                                        >
                                          {data.values.map((v, vi) => (
                                            <FormControlLabel
                                              key={vi}
                                              value={v.value}
                                              disabled={ocrApplied}
                                              control={<Radio size="small" sx={{ p: 0.25 }} />}
                                              label={
                                                <Typography variant="body2" fontWeight={sel.selectedValue === v.value ? 'bold' : 'normal'}>
                                                  {v.displayValue}
                                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                                    ({v.sources.map(s => getDocSourceLabel(s)).join(', ')})
                                                  </Typography>
                                                </Typography>
                                              }
                                              sx={{ m: 0, py: 0.25 }}
                                            />
                                          ))}
                                        </RadioGroup>
                                      )}
                                    </Box>
                                  </Box>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      )}

                      {/* Sezione: Indirizzo Residenza */}
                      {OCR_APPLIABLE_FIELDS.filter(f => f.category === 'indirizzo' && mergedOcrData.fields[f.key]).length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <HomeIcon fontSize="small" color="action" />
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" textTransform="uppercase">
                              {customerType === 'business' ? 'Residenza Firmatario' : 'Indirizzo Residenza'}
                            </Typography>
                          </Box>
                          <Grid container spacing={1}>
                            {OCR_APPLIABLE_FIELDS.filter(f => f.category === 'indirizzo' && mergedOcrData.fields[f.key]).map(field => {
                              const data = mergedOcrData.fields[field.key];
                              const sel = mergedOcrSelection[field.key] || {};
                              return (
                                <Grid item xs={data.conflict ? 12 : 6} sm={data.conflict ? 6 : (field.key === 'indirizzo' ? 6 : 3)} key={field.key}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'flex-start',
                                    bgcolor: sel.checked && !ocrApplied ? (data.conflict ? 'warning.light' : 'primary.50') : 'transparent',
                                    borderRadius: 1,
                                    border: data.conflict && !ocrApplied ? '1px solid' : 'none',
                                    borderColor: 'warning.main',
                                    p: 0.5,
                                    transition: 'background-color 0.2s',
                                  }}>
                                    {!ocrApplied && (
                                      <Checkbox
                                        size="small"
                                        checked={!!sel.checked}
                                        onChange={() => toggleMergedField(field.key)}
                                        sx={{ p: 0.25, mr: 0.5, mt: 0.25 }}
                                      />
                                    )}
                                    {ocrApplied && sel.checked && (
                                      <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 0.5, mt: 0.5 }} />
                                    )}
                                    <Box sx={{ flex: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">{field.label}</Typography>
                                        {data.conflict && (
                                          <Chip label="Conflitto" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                                        )}
                                      </Box>
                                      {!data.conflict ? (
                                        <Typography variant="body2" fontWeight="bold">
                                          {data.values[0].displayValue}
                                        </Typography>
                                      ) : (
                                        <RadioGroup
                                          value={sel.selectedValue || ''}
                                          onChange={(e) => selectConflictValue(field.key, e.target.value)}
                                        >
                                          {data.values.map((v, vi) => (
                                            <FormControlLabel
                                              key={vi}
                                              value={v.value}
                                              disabled={ocrApplied}
                                              control={<Radio size="small" sx={{ p: 0.25 }} />}
                                              label={
                                                <Typography variant="body2" fontWeight={sel.selectedValue === v.value ? 'bold' : 'normal'}>
                                                  {v.displayValue}
                                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                                    ({v.sources.map(s => getDocSourceLabel(s)).join(', ')})
                                                  </Typography>
                                                </Typography>
                                              }
                                              sx={{ m: 0, py: 0.25 }}
                                            />
                                          ))}
                                        </RadioGroup>
                                      )}
                                    </Box>
                                  </Box>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      )}

                      {/* Info Documento (per documento, solo informativa) */}
                      {mergedOcrData.infoByDoc.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <DescriptionIcon fontSize="small" color="action" />
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" textTransform="uppercase">
                              Info Documenti
                            </Typography>
                          </Box>
                          {mergedOcrData.infoByDoc.map((docInfo, di) => (
                            <Box key={di} sx={{ mb: 1 }}>
                              {mergedOcrData.totalDocs > 1 && (
                                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                                  {getDocSourceLabel(docInfo.documentId)}
                                  {docInfo.ocrEngine && ` — ${docInfo.ocrEngine.toUpperCase()}`}
                                  {docInfo.confidence != null && ` — Affidabilità: ${docInfo.confidence}%`}
                                </Typography>
                              )}
                              <Grid container spacing={1}>
                                {docInfo.fields.map(f => (
                                  <Grid item xs={6} sm={3} key={f.key}>
                                    <Box sx={{ p: 0.5 }}>
                                      <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                                      <Typography variant="body2">{f.value}</Typography>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Pulsante Applica */}
                      {!ocrApplied && (
                        <Button
                          variant="contained"
                          size="small"
                          disabled={!Object.values(mergedOcrSelection).some(s => s.checked)}
                          startIcon={<CheckCircleIcon />}
                          onClick={applyMergedOcrData}
                        >
                          {customerType === 'business' ? 'Applica Dati Firmatario al Form' : 'Applica Dati Selezionati al Form'}
                        </Button>
                      )}
                    </Paper>
                  )}

                  {/* Sezione: Documenti Aggiuntivi (senza OCR) */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mt: 3,
                      bgcolor: 'background.paper',
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <AttachFileIcon color="action" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Allega Altri Documenti
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Carica documenti aggiuntivi (contratti, moduli, ecc.) che verranno salvati nel fascicolo del cliente senza scansione OCR.
                    </Typography>

                    <TextField
                      label="Descrizione documento"
                      size="small"
                      fullWidth
                      value={extraDocDescription}
                      onChange={(e) => setExtraDocDescription(e.target.value)}
                      placeholder="Es: Contratto fornitura, Modulo privacy..."
                      sx={{ mb: 2 }}
                    />

                    <input
                      type="file"
                      ref={extraFileInputRef}
                      onChange={handleExtraDocUpload}
                      accept="image/jpeg,image/png,image/jpg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/zip"
                      style={{ display: 'none' }}
                      id="extra-doc-upload"
                      multiple
                    />
                    <label htmlFor="extra-doc-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={uploadingExtraDocs.length > 0 ? <CircularProgress size={20} /> : <AttachFileIcon />}
                        disabled={uploadingExtraDocs.length > 0}
                        size="small"
                      >
                        {uploadingExtraDocs.length > 0
                          ? `Caricamento ${uploadingExtraDocs.filter(f => f.status === 'done').length}/${uploadingExtraDocs.length}...`
                          : 'Allega File'}
                      </Button>
                    </label>

                    {/* Progresso upload */}
                    {uploadingExtraDocs.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {uploadingExtraDocs.map((f, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            {f.status === 'uploading' && <CircularProgress size={16} />}
                            {f.status === 'done' && <CheckCircleIcon fontSize="small" color="success" />}
                            {f.status === 'error' && <Typography color="error" fontSize="small">✗</Typography>}
                            <Typography variant="body2" color={f.status === 'error' ? 'error' : 'text.secondary'}>
                              {f.name} {f.status === 'error' ? `- ${f.error || 'Errore'}` : ''}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Lista documenti aggiuntivi caricati */}
                    {extraDocs.length > 0 && (
                      <List dense sx={{ mt: 1 }}>
                        {extraDocs.map((doc) => (
                          <ListItem key={doc.id}>
                            <ListItemIcon>
                              <AttachFileIcon color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary={doc.filename}
                              secondary={doc.description || `${(doc.size / 1024).toFixed(1)} KB`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => handleDeleteExtraDoc(doc)}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Paper>
                </>
              )}
            </CardContent>
          </Card>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Dati Anagrafici
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              {customerType === 'private' ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="first_name"
                      control={control}
                      rules={{ required: customerType === 'private' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nome"
                          fullWidth
                          required={customerType === 'private'}
                          error={!!errors.first_name}
                          helperText={errors.first_name?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="last_name"
                      control={control}
                      rules={{ required: customerType === 'private' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Cognome"
                          fullWidth
                          required={customerType === 'private'}
                          error={!!errors.last_name}
                        />
                      )}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <Controller
                    name="business_name"
                    control={control}
                    rules={{ required: customerType === 'business' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Ragione Sociale"
                        fullWidth
                        required={customerType === 'business'}
                        error={!!errors.business_name}
                      />
                    )}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} sm={6}>
                <Controller
                  name="fiscal_code"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Codice Fiscale"
                      fullWidth
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      error={cfValidation.valid === false}
                      helperText={
                        cfValidation.loading 
                          ? 'Validazione in corso...' 
                          : cfValidation.error 
                            ? cfValidation.error 
                            : cfValidation.valid 
                              ? 'Codice fiscale valido ✓' 
                              : 'Inserisci il CF per calcolare data e luogo di nascita'
                      }
                      InputProps={{
                        endAdornment: (
                          <>
                            {cfValidation.loading && <CircularProgress size={20} />}
                            {cfValidation.valid === true && (
                              <Tooltip title="Codice fiscale valido">
                                <CheckCircleIcon color="success" />
                              </Tooltip>
                            )}
                            {cfValidation.valid === false && (
                              <Tooltip title={cfValidation.error}>
                                <ErrorIcon color="error" />
                              </Tooltip>
                            )}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="vat_number"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Partita IVA"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="birth_date"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Data di Nascita"
                      fullWidth
                      disabled
                      helperText="Calcolata automaticamente dal CF"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Controller
                  name="birth_place"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Luogo di Nascita"
                      fullWidth
                      disabled
                      helperText="Calcolato automaticamente dal CF"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {customerType === 'business' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Firmatario Contratto
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="signer_role"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Ruolo / carica</InputLabel>
                        <Select {...field} label="Ruolo / carica">
                          {SIGNER_ROLE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                {signerRole === 'other' && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="signer_role_custom"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} label="Ruolo personalizzato" fullWidth />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="signer_first_name"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Nome" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="signer_last_name"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Cognome" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="signer_fiscal_code"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Codice Fiscale"
                        fullWidth
                        inputProps={{ maxLength: 16, style: { textTransform: 'uppercase' } }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="signer_birth_date"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Data di nascita"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="signer_birth_place"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Luogo di nascita" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="signer_address"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Indirizzo residenza" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Controller
                    name="signer_postal_code"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="CAP" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="signer_city"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Comune" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Controller
                    name="signer_province"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Provincia"
                        fullWidth
                        inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Indirizzo di Residenza
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="address_residenza"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Indirizzo Residenza"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="postal_code_residenza"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="CAP"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="city_residenza"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Località"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="province_residenza"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Provincia"
                      fullWidth
                      inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Recapito Bollette
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Indirizzo"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="postal_code"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="CAP"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Località"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="province"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Provincia"
                      fullWidth
                      inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Contatti
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Telefono"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="mobile"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Cellulare"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="fax"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Fax"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email (per invio fatture)"
                      type="email"
                      fullWidth
                      helperText="Indirizzo per l'invio delle fatture"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="email_secondaria"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email Secondaria"
                      type="email"
                      fullWidth
                      helperText="Indirizzo email alternativo"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="pec"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="PEC"
                      type="email"
                      fullWidth
                      helperText="Posta Elettronica Certificata"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="sdi_code"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Codice SDI"
                      fullWidth
                      inputProps={{ maxLength: 7, style: { textTransform: 'uppercase' } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="whatsapp_number"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Numero WhatsApp"
                      fullWidth
                      placeholder="+39 3XX XXX XXXX"
                      helperText="Numero per comunicazioni WhatsApp"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="whatsapp_opt_in"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Consenso WhatsApp"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Preferenze Fatturazione
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="invio_email"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Invio fatture via Email"
                    />
                  )}
                />
              </Grid>
              {watch('invio_email') && (
                <Grid item xs={12} sm={8}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: watch('invio_email') ? 'Email obbligatoria per invio fatture' : false,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Formato email non valido'
                      }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email per invio fatture"
                        fullWidth
                        required
                        error={!!errors.email}
                        helperText={errors.email?.message || 'Inserisci o modifica l\'email per l\'invio fatture'}
                        InputProps={{
                          startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <Controller
                  name="modalita_pagamento"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Modalità Pagamento</InputLabel>
                      <Select {...field} label="Modalità Pagamento">
                        <MenuItem value="bollettino">Bollettino Postale</MenuItem>
                        <MenuItem value="bonifico">Bonifico Bancario</MenuItem>
                        <MenuItem value="rid_sepa">RID SEPA</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              {modalitaPagamento === 'rid_sepa' && (
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="iban"
                    control={control}
                    rules={{
                      pattern: {
                        value: /^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/i,
                        message: 'Formato IBAN non valido'
                      }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="IBAN"
                        fullWidth
                        inputProps={{ maxLength: 34, style: { textTransform: 'uppercase' } }}
                        error={!!errors.iban}
                        helperText={errors.iban?.message || 'Obbligatorio per RID SEPA'}
                      />
                    )}
                  />
                </Grid>
              )}
              {modalitaPagamento === 'rid_sepa' && (
                <>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="mandato_sepa_id"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="ID Mandato SEPA"
                          fullWidth
                          inputProps={{ maxLength: 35 }}
                          helperText="Identificativo univoco del mandato di addebito"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="mandato_sepa_data"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Data Firma Mandato"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          helperText="Data di firma del mandato SEPA"
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="sconto_legge_448"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} color="success" />}
                      label={
                        <Box>
                          <Typography variant="body1">Sconto L.448/99 art.8 c.10</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Applica sconto di €0,076/litro (non imponibile) per famiglie numerose o disabili
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Note
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Note"
                  fullWidth
                  multiline
                  rows={4}
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Associa Contatore */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <MeterIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Contatore
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cerca e associa un contatore esistente a questo cliente. Puoi cercare per numero contatore, località o nome intestatario.
            </Typography>

            <Autocomplete
              options={meterOptions}
              value={selectedMeter}
              onChange={(event, newValue) => setSelectedMeter(newValue)}
              onInputChange={handleMeterInputChange}
              inputValue={meterInputValue}
              loading={meterSearchLoading}
              getOptionLabel={(option) => {
                const num = option.meter_number || option.nu_conta || '';
                const nome = option.c_nome || option.intestatario || '';
                const loc = option.c_localita || '';
                const citta = option.c_citta || '';
                const parts = [num, nome, loc, citta].filter(Boolean);
                return parts.join(' - ');
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" fontWeight="bold">
                      N° {option.meter_number || option.nu_conta}
                      {option.c_nome && <Typography component="span" variant="body2" color="text.secondary">{' — '}{option.c_nome}</Typography>}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[option.c_localita, option.c_citta, option.c_provinci].filter(Boolean).join(', ')}
                      {option.plant?.name && ` • Impianto: ${option.plant.name}`}
                    </Typography>
                    {option.customer && (
                      <Typography variant="caption" color="warning.main">
                        ⚠ Già associato a: {option.customer.ragione_sociale || `${option.customer.first_name || ''} ${option.customer.last_name || ''}`.trim()}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cerca Contatore"
                  size="small"
                  placeholder="Digita numero contatore, località o nome (min 2 caratteri)"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon fontSize="small" color="action" sx={{ ml: 0.5, mr: -0.5 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              noOptionsText={meterInputValue.length < 2 ? 'Digita almeno 2 caratteri' : 'Nessun contatore trovato'}
              filterOptions={(x) => x}
              fullWidth
            />

            {selectedMeter && (
              <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Contatore N° {selectedMeter.meter_number || selectedMeter.nu_conta}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[selectedMeter.c_localita, selectedMeter.c_citta, selectedMeter.c_provinci].filter(Boolean).join(', ')}
                    </Typography>
                    {selectedMeter.plant?.name && (
                      <Typography variant="caption" color="text.secondary">
                        Impianto: {selectedMeter.plant.name}
                      </Typography>
                    )}
                    {selectedMeter.customer && (
                      <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                        Questo contatore è già associato a un altro cliente. Verrà riassegnato.
                      </Alert>
                    )}
                  </Box>
                  <IconButton onClick={() => { setSelectedMeter(null); setMeterInputValue(''); }} size="small" color="error">
                    <LinkOffIcon />
                  </IconButton>
                </Box>
              </Paper>
            )}
          </CardContent>
        </Card>

        {/* Pulsanti Azione */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(isEdit ? `/crm/clienti/${id}` : '/crm/clienti')}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saveMutation.isLoading}
          >
            {saveMutation.isLoading ? 'Salvataggio...' : 'Salva'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default CustomerForm;
